<template>
    <div
        class="d-flex gap-3 w-100"
        :class="
            localField.type == 'divider'
                ? 'align-items-center'
                : 'align-items-start'
        "
        ref="wrapper"
    >
        <div class="flex-grow-1 min-w-0">
            <!-- Divider -->
            <div v-if="localField.type == 'divider'">
                <hr class="divider-line" />
            </div>
            <!-- Fields -->
            <div v-else-if="isField" class="d-flex flex-column gap-2">
                <!-- Field Label -->
                <div
                    v-if="
                        isBuilding &&
                        localField.isEditing &&
                        localField.type !== 'checkbox'
                    "
                    class="d-flex flex-wrap gap-2 align-items-start"
                >
                    <Field
                        class="flex-grow-1"
                        v-model="localField.label"
                        type="text"
                        :required="true"
                        :params="{ placeholder: 'Enter Question...' }"
                        ref="fieldLabel"
                        @keydown.enter.prevent="toggleEdit"
                    ></Field>
                    <button
                        v-if="localField.type == 'radio'"
                        title="Toggle layout"
                        class="btn btn-sm btn-white ms-auto"
                        style="border-radius: var(--radius-default)"
                        @click="toggleOrientation"
                    >
                        <span>
                            {{
                                localField.params.stacked
                                    ? 'Stacked'
                                    : 'Wrapped'
                            }}
                        </span>
                    </button>
                </div>

                <!-- Upload Field -->
                <div
                    v-if="localField.type == 'file-upload'"
                    class="d-flex flex-column gap-2"
                >
                    <div
                        v-if="!localField.isEditing"
                        class="d-flex align-items-baseline"
                    >
                        <span class="element-label">
                            {{ localField.label }}
                        </span>
                        <span v-if="localField.required">*</span>
                    </div>
                    <div class="file-card">
                        <AdvancedUploadField
                            class="w-100"
                            accept="image"
                            :multiple="true"
                            :max-image-height="3000"
                            :max-image-width="3000"
                            v-model="localField.value"
                            :required="localField.required"
                            :hide-upload-field="disabled"
                            :hide-delete-button="disabled"
                            :disabled="isBuilding"
                        />
                    </div>
                </div>
                <!-- Other Fields -->
                <div
                    v-else-if="
                        !localField.params?.options || !localField.isEditing
                    "
                    class="d-flex flex-column w-100 gap-2"
                >
                    <!-- Label row with Observation & Evidence toggle (choice fields with feature enabled) -->
                    <div
                        v-if="hasObservationEvidence"
                        class="d-flex align-items-center justify-content-between w-100"
                    >
                        <span class="col-form-label mb-0 me-2 min-w-0"
                            >{{ localField.label
                            }}{{ localField.required ? '*' : '' }}</span
                        >
                        <button
                            type="button"
                            class="icon-btn icon-btn-primary icon-btn-sm flex-shrink-0"
                            :aria-label="
                                observationEvidenceExpanded
                                    ? 'Collapse'
                                    : 'Expand'
                            "
                            :title="
                                observationEvidenceExpanded
                                    ? 'Collapse'
                                    : 'Expand'
                            "
                            @click="toggleObservationEvidenceExpanded"
                        >
                            <i
                                class="fi"
                                :class="
                                    observationEvidenceExpanded
                                        ? 'fi-chevron-up'
                                        : 'fi-chevron-down'
                                "
                            ></i>
                        </button>
                    </div>
                    <div class="d-flex align-items-center w-100 gap-2">
                        <Field
                            :class="
                                localField.type !== 'checkbox' ? 'w-100' : ''
                            "
                            v-model="localField.value"
                            :type="localField.type"
                            :label="
                                hasObservationEvidence
                                    ? ''
                                    : !localField.isEditing
                                    ? localField.label
                                    : localField.type !== 'checkbox'
                                    ? 'Default Value'
                                    : ''
                            "
                            :isEditing="localField.isEditing"
                            :required="localField.required"
                            :disabled="
                                disabled ||
                                (isBuilding && !localField.isEditing)
                            "
                            :params="scopedParams"
                            @update:model-value="syncRecommendationRequired"
                            @create-option="createOption"
                            @remove-option="removeOption"
                            @keydown.enter.prevent="toggleEdit"
                        />
                        <!-- Checkbox Label -->
                        <Field
                            v-if="
                                isBuilding &&
                                localField.isEditing &&
                                localField.type == 'checkbox'
                            "
                            v-model="localField.label"
                            type="text"
                            :required="true"
                            :params="{ placeholder: 'Enter Question...' }"
                            ref="fieldLabel"
                            @keydown.enter.prevent="toggleEdit"
                            class="w-100"
                        ></Field>

                        <Timer
                            v-if="localField.type == 'radio' && isTimer"
                            ref="timerComponent"
                            v-model="localField.timer"
                            :disabled="disabled || isBuilding"
                            @timer-started="handleTimerRunning(true)"
                            @timer-stopped="handleTimerRunning(false)"
                            @timer-reset="handleTimerRunning(false)"
                        ></Timer>
                    </div>
                </div>

                <!-- Observation & Evidence (choice fields with feature enabled) -->
                <div
                    v-if="hasObservationEvidence"
                    class="observation-evidence-section"
                    @invalid.capture="onObservationEvidenceInvalidCapture"
                >
                    <div
                        v-show="observationEvidenceExpanded"
                        class="observation-evidence-details"
                    >
                        <Field
                            v-if="isEvidenceReviewedEnabled"
                            label="Evidence Reviewed"
                            v-model="localField.evidenceReviewed"
                            type="text"
                            required
                            :disabled="disabled"
                            :params="{
                                placeholder: !disabled
                                    ? 'Enter evidence reviewed...'
                                    : '',
                            }"
                        />
                        <Field
                            v-if="isObservationRecommendationEnabled"
                            label="Observation"
                            v-model="localField.observation"
                            type="textarea"
                            required
                            :disabled="disabled"
                            :params="{
                                placeholder: !disabled
                                    ? 'Enter observation...'
                                    : '',
                            }"
                        />
                        <Field
                            v-if="showRecommendation"
                            label="Recommendation"
                            v-model="localField.recommendation"
                            type="textarea"
                            :required="recommendationFieldRequired"
                            :disabled="disabled"
                            :params="{
                                placeholder: !disabled
                                    ? 'Enter recommendation...'
                                    : '',
                            }"
                        />
                    </div>
                </div>

                <div
                    v-if="localField.params?.options && localField.isEditing"
                    class="d-flex flex-column gap-2"
                >
                    <div
                        v-for="(option, index) in localField.params.options"
                        :key="index"
                        class="d-flex gap-2 align-items-center"
                    >
                        <button
                            v-if="isScoring"
                            class="icon-btn icon-btn-xs"
                            :title="
                                option.isNA
                                    ? 'N/A: Does not affect score'
                                    : option.isCorrect
                                    ? 'Correct'
                                    : 'Incorrect'
                            "
                            @click="handleScoringRuleToggle(option.value)"
                            :style="
                                option.isNA
                                    ? 'background-color: var(--neutral-600); color: white; border: 1px dashed var(--neutral-800);'
                                    : option.isCorrect
                                    ? 'background-color: var(--success); color: white;'
                                    : 'background-color: var(--danger); color: white;'
                            "
                        >
                            <i
                                :class="
                                    option.isNA
                                        ? 'fi fi-dash'
                                        : option.isCorrect
                                        ? 'fi fi-check'
                                        : 'fi fi-x'
                                "
                            ></i>
                        </button>
                        <!-- Editable label input -->
                        <Field
                            class="w-100 ms-1"
                            v-model="option.label"
                            type="text"
                            :required="true"
                            :ref="'optionLabel-' + index"
                            :params="{
                                placeholder:
                                    option.placeholder || 'Enter Option...',
                            }"
                            @keydown.enter.prevent="toggleEdit"
                        ></Field>

                        <!-- Remove option button -->
                        <IconButton
                            v-if="index > 1"
                            preset="delete"
                            tooltip="Remove option"
                            @click="removeOption($event, option)"
                        />
                    </div>
                </div>
                <!-- Add Option + Scoring Controls -->
                <div
                    v-if="localField.isEditing"
                    class="d-flex flex-column gap-2 w-100"
                >
                    <div
                        v-if="
                            localField.params?.options || showPrioritySelector
                        "
                        class="d-flex justify-content-between align-items-center flex-wrap gap-2 w-100"
                    >
                        <button
                            v-if="localField.params?.options"
                            class="btn btn-white"
                            @click="handleAddOption"
                            style="width: fit-content"
                        >
                            Add Option
                            <i class="ml-1 fi fi-add add-option-icon"></i>
                        </button>

                        <div
                            v-if="showObservationRecommendationMetadataControls"
                            class="d-flex align-items-center flex-wrap gap-2"
                        >
                            <span class="element-label mb-0">Priority*</span>

                            <button
                                type="button"
                                class="priority-option-btn"
                                :class="{
                                    'priority-option-selected':
                                        localField.priority === 'LOW',
                                }"
                                @click="localField.priority = 'LOW'"
                            >
                                <i
                                    class="fi fi-priority-low"
                                    style="color: var(--success)"
                                ></i>
                                <span>Low</span>
                            </button>

                            <button
                                type="button"
                                class="priority-option-btn"
                                :class="{
                                    'priority-option-selected':
                                        localField.priority === 'MEDIUM',
                                }"
                                @click="localField.priority = 'MEDIUM'"
                            >
                                <i
                                    class="fi fi-priority-medium"
                                    style="color: var(--warning)"
                                ></i>
                                <span>Medium</span>
                            </button>

                            <button
                                type="button"
                                class="priority-option-btn"
                                :class="{
                                    'priority-option-selected':
                                        localField.priority === 'HIGH',
                                }"
                                @click="localField.priority = 'HIGH'"
                            >
                                <i
                                    class="fi fi-priority-high"
                                    style="color: var(--danger)"
                                ></i>
                                <span>High</span>
                            </button>

                            <button
                                type="button"
                                class="priority-option-btn"
                                :class="{
                                    'priority-option-selected':
                                        localField.priority === 'LEGISLATED',
                                }"
                                @click="localField.priority = 'LEGISLATED'"
                            >
                                <i
                                    class="fi fi-priority-very-high"
                                    style="color: var(--danger)"
                                ></i>
                                <span>Legislated</span>
                            </button>
                        </div>

                        <div
                            v-if="showObservationRecommendationMetadataControls"
                            style="min-width: 240px"
                        >
                            <Field
                                v-model="localField.category"
                                label="Category"
                                label-position="left"
                                type="select"
                                :params="categoryFieldParams"
                            />
                        </div>
                    </div>
                    <div
                        v-if="showObservationRecommendationMetadataControls"
                        class="oe-defaults-section d-flex flex-column w-100 pt-2 border-top"
                    >
                        <div class="oe-defaults-card">
                            <Field
                                v-if="isEvidenceReviewedEnabled"
                                label="Evidence Reviewed"
                                v-model="localField.evidenceReviewed"
                                type="text"
                                :params="{
                                    placeholder:
                                        'Enter default evidence reviewed...',
                                }"
                            />
                            <Field
                                v-if="isObservationRecommendationEnabled"
                                label="Recommendation"
                                v-model="localField.recommendation"
                                type="textarea"
                                :params="{
                                    placeholder:
                                        'Enter default recommendation...',
                                }"
                            />
                        </div>
                    </div>
                    <div
                        class="d-flex flex-wrap align-items-start w-100 pt-2 border-top"
                        style="row-gap: 0.5rem; column-gap: 1.5rem"
                    >
                        <div
                            v-if="isScoring"
                            class="d-flex flex-column align-items-center gap-2 me-auto"
                        >
                            <div class="d-flex w-100 align-items-center gap-2">
                                <label class="element-label">Points:</label>
                                <Field
                                    v-model="localField.max_score"
                                    type="number"
                                    :params="{
                                        min: 1,
                                        max: 100,
                                    }"
                                    style="width: 68px"
                                ></Field>
                                <ToolTip :max-width="14">
                                    <template #tooltip-trigger>
                                        <i
                                            class="fi fi-info"
                                            style="color: var(--primary-400)"
                                        ></i>
                                    </template>

                                    <template #tooltip-content>
                                        {{ pointsTooltipText }}
                                    </template>
                                </ToolTip>
                            </div>
                            <div
                                v-if="isTimer"
                                class="d-flex w-100 align-items-center gap-2"
                            >
                                <label class="element-label"
                                    >Timer Threshold:</label
                                >
                                <Field
                                    v-model="localField.threshold"
                                    type="number"
                                    :params="{
                                        min: 0,
                                        max: 10000,
                                    }"
                                    style="width: 68px"
                                ></Field>
                                <ToolTip :max-width="14">
                                    <template #tooltip-trigger>
                                        <i
                                            class="fi fi-info"
                                            style="color: var(--primary-400)"
                                        ></i>
                                    </template>

                                    <template #tooltip-content>
                                        {{ timerThresholdTooltipText }}
                                    </template>
                                </ToolTip>
                            </div>
                        </div>
                        <div
                            class="d-flex flex-wrap align-items-start"
                            style="row-gap: 0.5rem; column-gap: 1.5rem"
                        >
                            <Field
                                v-if="localField.type !== 'checkbox'"
                                label="Required?"
                                type="boolean"
                                v-model="localField.required"
                            ></Field>
                            <Field
                                v-if="showScoringToggle"
                                label="Score?"
                                type="boolean"
                                v-model="isScoring"
                                @update:model-value="handleScoringToggle"
                            ></Field>
                            <Field
                                v-if="localField.type == 'radio'"
                                label="Timer?"
                                type="boolean"
                                v-model="isTimer"
                                @update:model-value="handleTimerToggle"
                            ></Field>
                        </div>
                    </div>

                    <Field
                        v-if="
                            localField.type == 'checkbox' &&
                            localField.isEditing
                        "
                        v-model="hasConditionals"
                        label="Show notes if unchecked"
                        type="boolean"
                        @update:model-value="toggleConditional"
                    ></Field>
                </div>
                <Field
                    v-if="
                        localField.conditionals?.[0] &&
                        (localField.isEditing ||
                            localField.value ==
                                localField.conditionals?.[0].value)
                    "
                    v-model="localField.conditionals[0].field.value"
                    :label="localField.conditionals[0].field.label"
                    :type="localField.conditionals[0].field.type"
                    :disabled="disabled"
                ></Field>
            </div>
            <!-- Content -->
            <!-- h3 -->
            <div
                v-else-if="localField.type == 'h3'"
                class="d-flex align-items-center gap-2"
            >
                <h3 v-if="!localField.isEditing">{{ localField.label }}</h3>
                <textarea
                    v-else
                    v-model="localField.label"
                    :required="true"
                    placeholder="Enter Heading..."
                    ref="fieldLabel"
                    @keydown.enter.prevent="toggleEdit"
                    class="editable-textarea form-heading"
                    rows="1"
                ></textarea>
            </div>
            <!-- Paragraph -->
            <div
                v-else-if="localField.type == 'p'"
                class="d-flex align-items-center gap-2"
            >
                <p class="text-muted m-0" v-if="!localField.isEditing">
                    {{ localField.label }}
                </p>
                <textarea
                    v-else
                    v-model="localField.label"
                    :required="true"
                    placeholder="Enter Paragraph..."
                    ref="fieldLabel"
                    @keydown.enter.prevent="toggleEdit"
                    class="editable-textarea text-muted"
                    rows="1"
                ></textarea>
            </div>
            <!-- Add a URL -->
            <div
                v-else-if="localField.type == 'url'"
                class="d-flex flex-column justify-content-center gap-2"
            >
                <div class="d-flex align-items-center gap-2">
                    <a :href="localField.value" target="_blank">
                        {{ localField.label || 'Open Link' }}
                    </a>
                </div>
                <Field
                    v-if="localField.isEditing"
                    class="w-100"
                    v-model="localField.label"
                    label="Display Text"
                    :type="localField.type"
                    :params="{ placeholder: 'Enter value' }"
                />
                <Field
                    v-if="localField.isEditing"
                    class="w-100"
                    v-model="localField.value"
                    label="Link"
                    :type="localField.type"
                    :params="{ placeholder: 'Enter value' }"
                />
            </div>
            <!-- Upload an image -->
            <div
                v-else-if="
                    localField.type == 'upload-image' &&
                    (isBuilding || localField.value)
                "
                class="d-flex justify-content-center align-items-center gap-2"
            >
                <div class="d-flex flex-column gap-2 w-100">
                    <div class="d-flex">
                        <span v-if="isBuilding" class="element-label">
                            Upload an Image
                        </span>
                    </div>
                    <div
                        v-if="localField.isEditing || !localField.value"
                        class="d-flex flex-column gap-2"
                    >
                        <UploadField
                            class="w-100"
                            accept="image"
                            max-image-height="800"
                            max-image-width="400"
                            @uploaded="saveFile"
                        />
                    </div>
                    <ImageExpandable
                        v-else
                        :image-id="localField.value"
                        :no-thumb="true"
                    />
                </div>
            </div>
            <!-- Upload a file -->
            <div
                v-else-if="localField.type == 'upload-file'"
                class="d-flex justify-content-center align-items-center gap-2"
            >
                <div class="d-flex flex-column gap-2 w-100">
                    <div class="d-flex">
                        <span v-if="isBuilding" class="element-label">
                            Upload a File
                        </span>
                    </div>
                    <div class="file-card">
                        <AdvancedUploadField
                            class="w-100"
                            :multiple="true"
                            v-model="localField.value"
                            :hide-upload-field="
                                (!localField.isEditing &&
                                    localField.value[0]) ||
                                !isBuilding
                            "
                            :hide-delete-button="!isBuilding"
                            @update:modelValue="toggleEdit"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="d-flex flex-column align-items-end gap-2">
            <div
                v-if="
                    showEdit &&
                    !localField.isEditing &&
                    ((localField.max_score && localField.priority) ||
                        localField.category)
                "
                class="d-flex align-items-center justify-content-end gap-2"
            >
                <span v-if="localField.category" class="field-category-badge">
                    {{ categoryLabel(localField.category) }}
                </span>
                <template v-if="localField.max_score && localField.priority">
                    <i
                        v-if="localField.priority === 'MEDIUM'"
                        class="fi fi-priority-medium text-warning"
                    ></i>
                    <i
                        v-else-if="localField.priority === 'LEGISLATED'"
                        class="fi fi-priority-very-high text-danger"
                    ></i>
                    <i
                        v-else-if="localField.priority === 'HIGH'"
                        class="fi fi-priority-high text-danger"
                    ></i>
                    <i v-else class="fi fi-priority-low text-success"></i>
                </template>
            </div>
            <IconButton
                v-if="showEdit"
                :preset="localField.isEditing ? 'check' : 'edit'"
                :tooltip="
                    localField.isEditing ? 'Finish editing' : 'Edit element'
                "
                @click="toggleEdit"
            />
            <IconButton
                v-if="isBuilding"
                preset="delete"
                tooltip="Remove element"
                @click="$emit('remove', localField)"
            />
        </div>
    </div>
</template>

<script>
import Field from './Field.vue';
import IconButton from './IconButton.vue';
import AdvancedUploadField from './fields/AdvancedUploadField.vue';
import Timer from './fields/Timer.vue';
import ToolTip from './fields/ToolTip.vue';
import UploadField from './fields/UploadField.vue';
import ImageExpandable from './ImageExpandable.vue';
import {
    AUDIT_FIELD_CATEGORY_OPTIONS,
    AUDIT_FIELD_CATEGORY_UNCATEGORIZED,
    normalizeAuditFieldCategory,
} from '@/utils/auditCategories';
import {
    hasObservationEvidenceSelection,
    includesObservationEvidenceDefaults,
    isObservationEvidenceField,
    isObservationEvidenceFieldType,
    isRecommendationRequired,
    shouldShowRecommendation,
} from '@/utils/auditObservationEvidence';

// types representing fillable fields
const FIELD_TYPES = [
    'text',
    'textarea',
    'date',
    'select',
    'multi-select',
    'checkbox',
    'checkbox-group',
    'range',
    'radio',
    'file-upload',
];

export default {
    name: 'FieldBuilder',
    components: {
        Field,
        IconButton,
        UploadField,
        AdvancedUploadField,
        ImageExpandable,
        ToolTip,
        Timer,
    },
    props: {
        field: {
            type: Object,
            required: true,
        },
        // Activates builder mode so the element can be altered (changing fields properties, heading text, etc.)
        isBuilding: {
            type: Boolean,
            default: false,
        },
        // If true, disables all fields (for view-only)
        disabled: {
            type: Boolean,
            default: false,
        },
        // Panel ID to scope field names for fields in multi-panel scenarios
        panelId: {
            type: [Number, String],
            default: null,
        },
        expandObservationEvidence: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:field', 'remove'],
    data() {
        return {
            localField: null,
            isScoring: false,
            isTimer: false,
            hasConditionals: false,
            previousTimer: null, // stash for restoring
            previousThreshold: null, // stash for restoring
            timerRunning: false, // track timer state
            observationEvidenceExpanded: false,
            /**
             * After initial prop sync, value watcher may run with saved answers; keep O&E
             * collapsed until hydration completes, then allow auto-expand on user edits.
             */
            observationEvidenceAllowAutoExpand: false,
            /** Blocks trusted `invalid` bursts right after manual collapse (they would re-open O&E). */
            suppressObservationEvidenceAutoExpand: false,
            recommendationFieldRequired: false,
        };
    },
    watch: {
        // When parent prop changes, sync into local copy
        field: {
            handler(newField) {
                // Only update if the prop changes, preventing unnecessary mutation
                if (
                    JSON.stringify(newField) !== JSON.stringify(this.localField)
                ) {
                    this.localField = { ...newField };
                    this.resetObservationEvidenceAutoExpandGate();
                }
                this.syncRecommendationRequired();
            },
            deep: true,
            immediate: true,
        },
        isValid(val) {
            this.localField.isValid = val;
        },
        // When local copy mutates, emit update
        localField: {
            handler(updated) {
                this.syncRecommendationRequired();
                // Only emit update if there are actual changes (deep comparison)
                if (JSON.stringify(updated) !== JSON.stringify(this.field)) {
                    this.$emit(
                        'update:field',
                        this.sanitizeFieldPayload(updated)
                    );
                }
            },
            deep: true,
        },

        // Watch for field value changes to auto-start timer and auto-expand Observation & Evidence
        'localField.value': {
            handler(newValue, oldValue) {
                this.syncRecommendationRequired();
                if (this.isBuilding) return;
                // Auto-expand Observation & Evidence when a selection is made (not on initial load)
                if (this.hasObservationEvidence) {
                    if (!this.observationEvidenceAllowAutoExpand) {
                        return;
                    }
                    const isNowSelected = hasObservationEvidenceSelection(
                        this.localField
                    );
                    if (isNowSelected) {
                        this.observationEvidenceExpanded = true;
                    }
                }
                // Auto-start timer if not in building mode and timer is enabled
                if (this.isTimer && newValue !== oldValue) {
                    const isCorrectAnswer =
                        this.localField.scoring_rules?.includes(newValue);

                    if (isCorrectAnswer && !this.timerRunning) {
                        this.startTimer();
                    }
                }
            },
            deep: true,
        },
        'localField.observationAndRecommendation'(enabled) {
            if (!enabled && this.localField?.evidenceReviewedEnabled) {
                this.localField.evidenceReviewedEnabled = false;
            }
            const isApplicableField =
                this.isObservationRecommendationEnabled &&
                !!this.localField?.max_score &&
                isObservationEvidenceFieldType(this.localField.type);
            if (
                isApplicableField &&
                typeof this.localField.priority === 'undefined'
            ) {
                this.localField.priority = 'LOW';
            }
        },
        'localField.evidenceReviewedEnabled'(enabled) {
            if (enabled && !this.isObservationRecommendationEnabled) {
                this.localField.evidenceReviewedEnabled = false;
            }
        },
        expandObservationEvidence: {
            immediate: true,
            handler(shouldExpand) {
                if (
                    shouldExpand &&
                    this.hasObservationEvidence &&
                    !this.observationEvidenceExpanded
                ) {
                    this.observationEvidenceExpanded = true;
                }
            },
        },
    },
    computed: {
        isField() {
            return FIELD_TYPES.includes(this.localField?.type);
        },
        isObservationRecommendationEnabled() {
            return this.localField?.observationAndRecommendation === true;
        },
        isEvidenceReviewedEnabled() {
            if (!this.isObservationRecommendationEnabled) return false;
            if (typeof this.localField?.evidenceReviewedEnabled === 'boolean') {
                return this.localField.evidenceReviewedEnabled;
            }
            return false;
        },
        hasObservationEvidence() {
            if (this.isBuilding) return false;
            return isObservationEvidenceField(this.localField);
        },
        showObservationRecommendationMetadataControls() {
            if (
                !this.localField?.isEditing ||
                !this.localField?.max_score ||
                !this.isObservationRecommendationEnabled
            ) {
                return false;
            }
            if (this.localField.type === 'range') {
                return true;
            }
            return (
                Array.isArray(this.localField?.params?.options) &&
                (this.localField.type === 'radio' ||
                    this.localField.type === 'checkbox-group')
            );
        },
        categoryFieldParams() {
            return {
                options: AUDIT_FIELD_CATEGORY_OPTIONS,
                placeholder: 'Select category...',
            };
        },
        showRecommendation() {
            if (!this.hasObservationEvidence) return false;
            return shouldShowRecommendation(this.localField);
        },
        showEdit() {
            if (this.localField.type == 'divider') {
                return false;
            }
            return this.isBuilding;
        },
        isRangeField() {
            return this.localField?.type === 'range';
        },
        pointsTooltipText() {
            if (this.isRangeField) {
                return 'Maximum points for this question. The earned score follows the slider (i.e. halfway across earns half of the points).';
            }
            if (this.isTimer) {
                return 'Maximum points for this question. Half are awarded for a correct answer; the rest depend on completion time (see Timer Threshold).';
            }
            return 'Maximum points for this question. Selecting any correct answer awards the full amount.';
        },
        timerThresholdTooltipText() {
            return 'Minimum time in seconds the timer must run to earn the full time-based portion of the points. Stopping before this threshold earns less.';
        },
        showScoringToggle() {
            return !!this.localField?.params?.options || this.isRangeField;
        },
        scopedParams() {
            const fieldType = this.localField?.type;

            if (fieldType === 'range') {
                const p = this.localField?.params || {};
                return {
                    ...p,
                    min: Number(p.min ?? 0),
                    max: Number(p.max ?? 100),
                    step: Number(p.step ?? 1),
                };
            }

            // Scope field names with panelId for radio/checkbox-group fields to prevent interference between panels
            if (this.panelId == null || !this.localField?.params) {
                return this.localField?.params;
            }

            if (fieldType === 'radio' || fieldType === 'checkbox-group') {
                const params = { ...this.localField.params };
                // Use existing name if provided, otherwise slugify the label (matching Field.vue behavior)
                const originalName =
                    params.name ||
                    (this.localField.label
                        ? this.localField.label
                              .replaceAll(' ', '-')
                              .toLowerCase()
                        : this.localField.id || `field-${Date.now()}`);
                // Add panelId prefix to field name
                params.name = `panel-${this.panelId}-${
                    originalName || `field-${Date.now()}`
                }`;
                return params;
            }

            return this.localField.params;
        },
        isValid() {
            if (this.isField) {
                // check label
                const trimmedLabel = this.localField.label?.trim();
                if (!trimmedLabel) return false;

                // check options if present
                if (this.localField.params?.options?.length) {
                    const hasValidOptions =
                        this.localField.params.options.every((o) =>
                            o.label?.trim()
                        );
                    if (!hasValidOptions) return false;
                }
                if (
                    this.showObservationRecommendationMetadataControls &&
                    !this.localField?.priority
                ) {
                    return false;
                }
                if (this.localField.type === 'range' && this.isScoring) {
                    const pts = Number(this.localField.max_score);
                    if (!Number.isFinite(pts) || pts < 1) {
                        return false;
                    }
                }
                // check file upload
            } else if (this.localField.type === 'upload-file') {
                // require at least one file
                if (
                    !Array.isArray(this.localField.value) ||
                    this.localField.value.length === 0
                ) {
                    return false;
                }
                // check image upload
            } else if (this.localField.type === 'upload-image') {
                if (!this.localField.value) return false;
            } else if (
                this.localField.type === 'h3' ||
                this.localField.type === 'p'
            ) {
                if (!this.localField.label) return false;
            }
            return true;
        },
    },
    methods: {
        syncRecommendationRequired() {
            this.recommendationFieldRequired = isRecommendationRequired(
                this.localField
            );
        },
        sanitizeFieldPayload(field) {
            const payload = JSON.parse(JSON.stringify(field));
            const observationAndRecommendationEnabled =
                payload?.observationAndRecommendation === true;
            const evidenceReviewedEnabled =
                observationAndRecommendationEnabled &&
                (typeof payload?.evidenceReviewedEnabled === 'boolean'
                    ? payload.evidenceReviewedEnabled
                    : false);
            payload.observationAndRecommendation =
                observationAndRecommendationEnabled;
            payload.evidenceReviewedEnabled = evidenceReviewedEnabled;
            const includePriority =
                !!payload?.max_score &&
                observationAndRecommendationEnabled &&
                includesObservationEvidenceDefaults(payload);
            const includeCategory = includePriority;

            if (!includePriority && 'priority' in payload) {
                delete payload.priority;
            }
            if (includeCategory && payload.category != null) {
                const normalizedCategory = normalizeAuditFieldCategory(
                    payload.category
                );
                if (normalizedCategory === AUDIT_FIELD_CATEGORY_UNCATEGORIZED) {
                    delete payload.category;
                } else {
                    payload.category = normalizedCategory;
                }
            } else if ('category' in payload) {
                delete payload.category;
            }
            if (
                (!includePriority || !evidenceReviewedEnabled) &&
                'evidenceReviewed' in payload
            ) {
                delete payload.evidenceReviewed;
            }
            if (!includePriority && 'recommendation' in payload) {
                delete payload.recommendation;
            }
            if (!includePriority && 'observation' in payload) {
                delete payload.observation;
            }

            if (payload?.type === 'range' && 'scoring_rules' in payload) {
                delete payload.scoring_rules;
            }

            return payload;
        },
        toggleEdit(silent = false) {
            if (this.localField.isEditing) {
                if (!this.isValid) {
                    if (!silent) {
                        // Explicit close (✓ button): redirect focus to the first invalid field
                        if (!this.localField.label?.trim()) {
                            this.$refs.fieldLabel?.focus?.();
                        } else {
                            // find first invalid option
                            const idx =
                                this.localField.params?.options?.findIndex(
                                    (o) => !o.label?.trim()
                                );
                            if (idx >= 0)
                                this.$refs[`optionLabel-${idx}`]?.focus?.();
                        }
                    }
                    // In both cases: don't close — keep field open
                    return;
                }
            }

            // toggle normally if valid
            if (this.isBuilding) {
                this.localField.isEditing = !this.localField.isEditing;
            } else {
                this.localField.isEditing = false;
            }

            if (this.localField.isEditing) {
                this.$nextTick(() => {
                    this.$refs.fieldLabel?.focus?.();
                });
            }
        },
        toggleOrientation() {
            this.localField.params.stacked = !this.localField.params.stacked;
        },
        resetObservationEvidenceAutoExpandGate() {
            this.observationEvidenceAllowAutoExpand = false;
            this.$nextTick(() => {
                this.$nextTick(() => {
                    this.observationEvidenceAllowAutoExpand = true;
                });
            });
        },
        toggleObservationEvidenceExpanded() {
            const nextExpanded = !this.observationEvidenceExpanded;
            this.observationEvidenceExpanded = nextExpanded;
            if (!nextExpanded) {
                this.suppressObservationEvidenceAutoExpand = true;
                this.$nextTick(() => {
                    requestAnimationFrame(() => {
                        this.suppressObservationEvidenceAutoExpand = false;
                    });
                });
            }
        },
        onObservationEvidenceInvalidCapture(event) {
            if (this.suppressObservationEvidenceAutoExpand && event.isTrusted) {
                return;
            }
            if (!this.observationEvidenceExpanded) {
                this.observationEvidenceExpanded = true;
            }
        },
        generateUniqueValue() {
            return Math.random().toString(36).substring(2, 10); // 8 characters, e.g., "a93kfj2d"
        },
        createOption(newOptionValue) {
            this.localField.params.options.push({
                label: newOptionValue,
                value: newOptionValue,
            });
        },
        removeOption(event, optionToRemove) {
            event.stopPropagation();
            this.localField.params.options =
                this.localField.params.options.filter(
                    (opt) => opt.value !== optionToRemove.value
                );
        },
        handleAddOption() {
            const newIndex = this.localField.params.options.length + 1;
            const newOption = {
                label: '',
                value: this.generateUniqueValue(),
                isCorrect: false,
                placeholder: `Option ${newIndex}`,
            };
            this.localField.params.options.push(newOption);
            this.$nextTick(() => {
                const lastIdx = this.localField.params.options.length - 1;
                this.$refs[`optionLabel-${lastIdx}`]?.focus?.();
            });
        },
        toggleConditional() {
            const conditionalNotes = {
                value: false,
                field: {
                    id: Date.now(),
                    type: 'textarea',
                    label: 'Notes',
                    value: null,
                },
            };

            if (this.hasConditionals) {
                this.localField.conditionals = [conditionalNotes];
            } else {
                this.localField.conditionals = [];
            }
        },
        handleTimerToggle(value) {
            if (value) {
                // Restore previous values if we have them, otherwise set defaults
                this.localField.timer = this.previousTimer ?? 0;
                this.localField.threshold = this.previousThreshold ?? 0;
                this.timerRunning = false; // Reset timer state when re-enabling
            } else {
                // Stash current values in case user re-enables later
                this.previousTimer ??= this.localField.timer;
                this.previousThreshold ??= this.localField.threshold;

                // Explicitly null out for backend
                this.localField.timer = null;
                this.localField.threshold = null;
                this.timerRunning = false; // Stop timer when disabling
            }
        },
        handleScoringToggle(value) {
            if (value) {
                this.localField.max_score = 1;
                if (this.localField.type !== 'range') {
                    if (!this.localField.scoring_rules) {
                        this.localField.scoring_rules = [];
                    }
                }
            } else {
                delete this.localField.max_score;
                if (this.localField.scoring_rules) {
                    delete this.localField.scoring_rules;
                }
                if ('priority' in this.localField) {
                    delete this.localField.priority;
                }
                if ('category' in this.localField) {
                    delete this.localField.category;
                }
                if (
                    this.localField.params &&
                    Array.isArray(this.localField.params.options)
                ) {
                    this.localField.params.options.forEach((option) => {
                        if ('isCorrect' in option) {
                            delete option.isCorrect;
                        }
                    });
                }
            }
        },
        handleScoringRuleToggle(value) {
            const option = this.localField.params.options.find(
                (opt) => opt.value === value
            );

            if (!option) return;

            if (option.isNA) {
                // Return to incorrect
                option.isNA = false;
                option.isCorrect = false;
            } else if (option.isCorrect) {
                // Move to NA
                option.isCorrect = false;
                option.isNA = true;
                // Remove from scoring rules since it's now NA
                this.localField.scoring_rules =
                    this.localField.scoring_rules.filter(
                        (val) => val !== value
                    );
            } else {
                // Move to Correct
                option.isCorrect = true;
                if (!this.localField.scoring_rules.includes(value)) {
                    this.localField.scoring_rules.push(value);
                }
            }
        },
        saveFile(response, file) {
            this.localField.value = response.data.id;
            this.localField.isEditing = false;
        },
        handleClickOutside(event) {
            const clickedInside = this.$refs.wrapper.contains(event.target);
            const clickedInsideDropdown = event.target.closest?.(
                '.selection-dropdown'
            );
            if (
                !clickedInside &&
                !clickedInsideDropdown &&
                this.localField.isEditing
            ) {
                // silent=true: don't steal focus back if invalid — let the user
                // go wherever they clicked (e.g. a newly added field's label)
                this.toggleEdit(true);
                this.$emit(
                    'update:field',
                    this.sanitizeFieldPayload(this.localField)
                );
            }
        },
        startTimer() {
            if (this.$refs.timerComponent && !this.timerRunning) {
                // Create a mock event object to prevent errors
                const mockEvent = {
                    stopPropagation: () => {},
                };
                this.$refs.timerComponent.toggleTimer(mockEvent);
                this.timerRunning = true;
            }
        },
        handleTimerRunning(isRunning) {
            this.timerRunning = isRunning;
        },
        categoryLabel(category) {
            const normalized = normalizeAuditFieldCategory(category);
            return (
                AUDIT_FIELD_CATEGORY_OPTIONS.find(
                    (option) => option.value === normalized
                )?.label || 'Uncategorized'
            );
        },
    },
    created() {
        this.localField = { ...this.field };
        this.isScoring = !!this.field.max_score;

        // Ensure legacy data with scoring has isCorrect defined
        if (
            this.isScoring &&
            this.localField.params?.options &&
            this.localField.type !== 'range'
        ) {
            this.localField.params.options.forEach((option) => {
                if (typeof option.isCorrect === 'undefined') {
                    option.isCorrect = false;
                }
            });
        }

        // Ensure legacy Observation & Evidence fields have a default priority
        const needsDefaultPriority =
            this.isObservationRecommendationEnabled &&
            !!this.localField?.max_score &&
            isObservationEvidenceFieldType(this.localField.type);
        if (
            needsDefaultPriority &&
            typeof this.localField.priority === 'undefined'
        ) {
            this.localField.priority = 'LOW';
        }
        this.hasConditionals =
            Array.isArray(this.field.conditionals) &&
            this.field.conditionals.length > 0;

        // Determine if timer is active
        if (typeof this.field.timer === 'number') {
            this.isTimer = true;
            this.previousTimer = this.field.timer;
            this.previousThreshold = this.field.threshold ?? 0;
        } else {
            this.isTimer = false;
        }
        this.syncRecommendationRequired();
    },
    mounted() {
        this.$nextTick(() => {
            const el = this.$refs['fieldLabel'];
            if (el) {
                el.focus();
            }
        });
        document.addEventListener('mousedown', this.handleClickOutside);
    },
    beforeUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    },
};
</script>

<style scoped>
.divider-line {
    border: none;
    border-top: 1px solid var(--neutral-700);
    margin: 0;
    width: 100%;
}

.element-label {
    color: var(--Colours-Neutral-900, #474747);
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 100%;
}

.checkbox-label {
    color: var(--neutral-800);
    font-size: 0.875rem;
    font-weight: 400 !important;
    font-weight: 600;
    line-height: 100%;
}

.editable-textarea {
    width: 100%;
    min-height: 0.875rem;
    /* sets the one-line height to match the static label */
    resize: none;
    border: none;
    padding: 0;
    margin: 0;
    outline: none;
    font-weight: 550;
    line-height: 100%;
    background: transparent;
    overflow: hidden;
}

.form-heading {
    font-size: 1.125rem;
    line-height: var(--space-6);
    font-family: Montserrat, sans-serif;
}

.icon-btn-xs {
    display: flex;
    width: 1.375rem;
    height: 1.375rem;
    padding: 0.4375rem 0.625rem;
    justify-content: center;
    align-items: center;
    gap: 0.625rem;
    border-radius: var(--Radius-radius-sm, 0.25rem);
    font-size: 0.875rem;
}

.close-icon {
    color: var(--neutral-800);
    transition: transform 0.15s ease, color 0.15s ease;
}

.close-icon:hover {
    color: var(--neutral-900);
    /* slightly darker for emphasis */
}

.range-container {
    margin-top: 1.5rem;
    position: relative;
    width: 100%;
}

.score-display {
    position: absolute;
    top: -1.5rem;
    right: 0;
    font-size: 0.875rem;
    line-height: 0.875rem;
    padding: 0 0.75rem;
}

.file-card {
    width: 100%;
    padding: var(--space-3);
    gap: 0.75rem;
    background-color: var(--neutral-400);
    border-radius: var(--space-3);
}

.observation-evidence-section .observation-evidence-details {
    margin-top: var(--space-2);
}

.observation-evidence-details {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    background-color: var(--neutral-400);
    padding: var(--space-3);
    border-radius: var(--radius-sm);
    border: 1px solid var(--neutral-500);
}

/* Nested surfaces (same stack as secondments SymptomForm / OtherAuditItem) */
.oe-defaults-section {
    gap: var(--space-2);
}

.oe-defaults-card {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    background-color: var(--neutral-500);
    padding: var(--space-3);
    border-radius: var(--radius-sm);
    border: 1px solid var(--neutral-600);
}

.min-w-0 {
    min-width: 0;
}

.add-option-icon {
    color: var(--primary-500);
}

.btn:hover .add-option-icon {
    color: white;
}

.priority-option-btn {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    border: 1px solid var(--neutral-700);
    background: var(--neutral-100);
    border-radius: var(--radius-default);
    padding: 0.25rem 0.5rem;
    color: var(--neutral-900);
    font-size: 0.75rem;
    line-height: 1;
}

.priority-option-selected {
    border-color: var(--primary-500);
    background: var(--primary-50);
    font-weight: 600;
}

.field-category-badge {
    max-width: 7.5rem;
    padding: 0.1875rem 0.375rem;
    border-radius: var(--radius-default);
    background: var(--primary-50);
    color: var(--primary-700);
    font-size: 0.7rem;
    line-height: 1.2;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
